import React, { useEffect, useRef } from 'react';
import './FloatingChatAnimation.css';

const FloatingChatAnimation = () => {
  const chatContainerRef = useRef(null);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    let isRunning = true;

    const singleCheck = `<svg class="w-4 h-4 text-blue-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const doubleCheck = `<svg class="w-4 h-4 text-blue-200 tick-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L7 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 10l-3.5 3.5M11 17l4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    // Three-scene script: text → invoice photo → voice message
    const script = [
      // ── SCENE 1: Text message ──────────────────────────
      { type: 'user', text: 'Gasté $25.000 en un café y un postre ☕️🍰', delay: 800 },
      { type: 'read', delay: 700 },
      { type: 'typing', delay: 700 },
      { type: 'bot', text: '¡Anotado! 📝 Guardé <strong>$25.000</strong> en <span class="text-[#396BE9] font-semibold">Cafés/Antojos</span>.', delay: 1600 },
      { type: 'typing', delay: 900 },
      { type: 'bot_card', delay: 2200 },

      // ── SCENE 2: Invoice photo ─────────────────────────
      { type: 'user_photo', delay: 2800 },
      { type: 'read', delay: 700 },
      { type: 'typing', delay: 1000 },
      { type: 'bot', text: '📸 ¡Factura detectada! Leí <strong>4 ítems</strong> del Restaurante El Sabor Costeño.', delay: 1800 },
      { type: 'typing', delay: 700 },
      { type: 'bot_invoice_card', delay: 2000 },

      // ── SCENE 3: Voice message ─────────────────────────
      { type: 'user_audio', delay: 2600 },
      { type: 'read', delay: 700 },
      { type: 'typing', delay: 1100 },
      { type: 'bot', text: '🎙️ Escuché tu audio: <strong>$85.000 en supermercado</strong> — ¡Ya está guardado! 🛒', delay: 1800 },
      { type: 'typing', delay: 700 },
      { type: 'bot_balance', delay: 1800 },

      { type: 'clear', delay: 7000 },
    ];

    function getCurrentTime() {
      const now = new Date();
      return `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`;
    }

    // Animated waveform bars (SVG-based, CSS-animated)
    function buildWaveform(color = '#396BE9', barCount = 20) {
      const heights = [3,5,8,12,16,20,14,9,18,22,17,11,7,15,20,13,8,5,9,4];
      const bars = heights.slice(0, barCount).map((h, i) => {
        const animDelay = (i * 0.07).toFixed(2);
        return `<rect x="${i * 5}" y="${(22 - h) / 2}" width="3" height="${h}" rx="1.5" fill="${color}" class="wv-bar" style="animation-delay:${animDelay}s"/>`;
      }).join('');
      return `<svg viewBox="0 0 100 22" height="22" width="100" style="overflow:visible">${bars}</svg>`;
    }

    const generators = {
      user: (text) => `
        <div class="msg-bubble msg-user max-w-[85%] bg-[#396BE9] text-white px-5 py-3 rounded-2xl rounded-br-sm shadow-[0_8px_30px_rgba(57,107,233,0.2)] relative text-[15px] font-medium border border-[#396BE9]/20">
          ${text}
          <div class="flex justify-end items-center gap-1 mt-1 -mb-1 float-right ml-3">
            <span class="text-[10px] text-blue-100/80 font-medium">${getCurrentTime()}</span>
            <span class="status-icon">${singleCheck}</span>
          </div>
          <div class="clear-both"></div>
        </div>
      `,

      user_photo: () => `
        <div class="msg-bubble msg-user max-w-[72%] rounded-2xl rounded-br-sm shadow-[0_8px_30px_rgba(57,107,233,0.25)] overflow-hidden relative border-2 border-[#396BE9]/30" style="background:#1d3a8a">
          <div class="relative">
            <img src="/landing/invoice-thumb.jpg" alt="Factura" class="w-full object-cover block" style="max-height:160px;object-position:center top;filter:brightness(0.92)"/>
            <div class="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40"></div>
            <div class="absolute bottom-2 left-3 right-3 flex items-center justify-between">
              <div class="flex items-center gap-1.5 bg-black/50 rounded-full px-2 py-0.5 backdrop-blur-sm">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="white" stroke-width="2"/><path d="m9 9 3 3 3-3M12 12v6" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>
                <span style="font-size:10px;color:white;font-weight:600">Factura</span>
              </div>
            </div>
          </div>
          <div class="px-3 py-2 flex justify-end items-center gap-1" style="background:#1d3a8a">
            <span class="text-[10px] font-medium" style="color:rgba(255,255,255,0.7)">${getCurrentTime()}</span>
            <span class="status-icon">${singleCheck}</span>
          </div>
        </div>
      `,

      user_audio: () => {
        const waveBlue = buildWaveform('rgba(255,255,255,0.9)');
        return `
          <div class="msg-bubble msg-user max-w-[80%] bg-[#396BE9] rounded-2xl rounded-br-sm shadow-[0_8px_30px_rgba(57,107,233,0.2)] border border-[#396BE9]/20 px-3 py-3">
            <div class="flex items-center gap-3">
              <!-- Mic icon -->
              <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="9" y="2" width="6" height="11" rx="3" fill="white"/>
                  <path d="M5 10a7 7 0 0 0 14 0" stroke="white" stroke-width="2" stroke-linecap="round"/>
                  <line x1="12" y1="19" x2="12" y2="22" stroke="white" stroke-width="2" stroke-linecap="round"/>
                  <line x1="8" y1="22" x2="16" y2="22" stroke="white" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              <!-- Waveform -->
              <div class="flex-1 flex flex-col gap-1">
                <div class="waveform-playing">
                  ${waveBlue}
                </div>
                <span class="text-[10px] text-blue-100/70 font-medium">0:08</span>
              </div>
              <!-- Time + check -->
              <div class="flex flex-col items-end ml-1">
                <span class="text-[10px] text-blue-100/70 font-medium">${getCurrentTime()}</span>
                <span class="status-icon">${singleCheck}</span>
              </div>
            </div>
          </div>
        `;
      },

      bot: (text) => `
        <div class="msg-bubble msg-bot max-w-[85%] bg-white text-[#1e293b] px-5 py-3 rounded-2xl rounded-bl-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] relative text-[15px] border border-slate-100">
          ${text}
          <div class="flex justify-end items-center gap-1 mt-1 -mb-1 float-right ml-3">
            <span class="text-[10px] text-slate-400 font-medium">${getCurrentTime()}</span>
          </div>
          <div class="clear-both"></div>
        </div>
      `,

      typing: () => `
        <div class="msg-bubble msg-bot typing-indicator bg-white px-5 py-4 rounded-2xl rounded-bl-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-slate-100 w-fit">
          <div class="flex gap-1.5 items-center h-2">
            <div class="w-2 h-2 bg-slate-400 rounded-full typing-dot"></div>
            <div class="w-2 h-2 bg-slate-400 rounded-full typing-dot"></div>
            <div class="w-2 h-2 bg-slate-400 rounded-full typing-dot"></div>
          </div>
        </div>
      `,

      bot_card: () => `
        <div class="msg-bubble msg-bot w-[90%] bg-white p-5 rounded-3xl rounded-bl-sm shadow-[0_12px_40px_rgba(0,0,0,0.06)] border border-slate-100/80 relative text-[15px]">
          <div class="flex justify-between items-center mb-4">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-blue-50 text-[#396BE9] flex items-center justify-center text-sm shadow-sm border border-blue-100/50">💰</div>
              <span class="text-slate-500 text-[11px] font-bold uppercase tracking-wider">Presupuesto Actual</span>
            </div>
          </div>
          <div class="text-3xl font-extrabold text-[#1e293b] mb-1 tracking-tight">$125.000</div>
          <div class="text-xs text-[#22c55e] font-semibold mb-4 bg-green-50 w-fit px-2 py-1 rounded-md">Disponible esta semana</div>
          <div class="w-full bg-slate-100 rounded-full h-2.5 mb-2 mt-3 overflow-hidden shadow-inner">
            <div class="bg-[#396BE9] h-full rounded-full transition-all duration-1000 ease-out" style="width: 0%" id="progress-bar"></div>
          </div>
          <div class="flex justify-between text-[12px] text-slate-400 font-medium">
            <span>Gastado: <strong class="text-slate-600">45%</strong></span>
            <span>Límite: $230.000</span>
          </div>
        </div>
      `,

      bot_invoice_card: () => `
        <div class="msg-bubble msg-bot w-[92%] bg-white rounded-2xl rounded-bl-sm shadow-[0_12px_40px_rgba(0,0,0,0.06)] border border-slate-100 overflow-hidden text-[13px]">
          <!-- Header -->
          <div class="flex items-center gap-2 px-4 py-3 border-b border-slate-100" style="background:linear-gradient(135deg,#EEF3FD,#F8FAFF)">
            <div class="w-8 h-8 rounded-full flex items-center justify-center text-base" style="background:#E1E9FC">🧾</div>
            <div>
              <div class="font-bold text-[#1B2559] text-[13px]">Factura registrada</div>
              <div class="text-slate-400 text-[10px]">Restaurante El Sabor Costeño</div>
            </div>
            <div class="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full" style="background:#ECFDF5;color:#059669">✓ Guardado</div>
          </div>
          <!-- Items -->
          <div class="px-4 py-2 space-y-1.5">
            <div class="flex justify-between text-[12px]"><span class="text-slate-600">🥘 Bandeja Paisa</span><span class="font-semibold text-slate-700">$26.000</span></div>
            <div class="flex justify-between text-[12px]"><span class="text-slate-600">🥤 Aguapanela (×2)</span><span class="font-semibold text-slate-700">$9.000</span></div>
            <div class="flex justify-between text-[12px]"><span class="text-slate-600">🍮 Postre de Nata</span><span class="font-semibold text-slate-700">$9.000</span></div>
            <div class="flex justify-between text-[12px]"><span class="text-slate-600">💳 Propina</span><span class="font-semibold text-slate-700">$3.500</span></div>
          </div>
          <!-- Total -->
          <div class="flex justify-between items-center px-4 py-2.5 border-t border-slate-100" style="background:#F8FAFF">
            <span class="font-bold text-slate-500 text-[12px]">TOTAL</span>
            <span class="font-extrabold text-[#396BE9] text-[16px]">$47.500</span>
          </div>
        </div>
      `,

      bot_balance: () => `
        <div class="msg-bubble msg-bot w-[90%] bg-white p-4 rounded-2xl rounded-bl-sm shadow-[0_12px_40px_rgba(0,0,0,0.06)] border border-slate-100 text-[13px]">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-7 h-7 rounded-full flex items-center justify-center text-sm" style="background:#FFF3E0">📊</div>
            <span class="font-bold text-[#1B2559] text-[12px] uppercase tracking-wide">Resumen del día</span>
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div class="rounded-xl p-2.5" style="background:#F0FDF4">
              <div class="text-[10px] text-slate-400 font-semibold">Ingresos</div>
              <div class="font-extrabold text-[#16a34a] text-[15px]">$1.800.000</div>
            </div>
            <div class="rounded-xl p-2.5" style="background:#FFF5F5">
              <div class="text-[10px] text-slate-400 font-semibold">Gastos hoy</div>
              <div class="font-extrabold text-[#ef4444] text-[15px]">$157.500</div>
            </div>
          </div>
          <div class="mt-3 text-center text-[11px] font-semibold" style="color:#396BE9">
            💪 ¡Vas bien, te quedan $1.642.500!
          </div>
        </div>
      `
    };

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    async function runAnimationSequence() {
      while (isRunning) {
        // Fade out old messages
        const oldMessages = chatContainer.querySelectorAll('.msg-bubble');
        oldMessages.forEach(msg => {
          msg.classList.add('fade-up');
          setTimeout(() => { if (isRunning && msg.parentNode) msg.remove(); }, 800);
        });
        if (oldMessages.length > 0) await sleep(800);

        for (const action of script) {
          if (!isRunning) return;
          await sleep(action.delay);
          if (!isRunning) return;

          if (action.type === 'clear') continue;

          if (action.type === 'read') {
            const userMsgs = chatContainer.querySelectorAll('.msg-user .status-icon');
            if (userMsgs.length > 0) {
              const lastMsg = userMsgs[userMsgs.length - 1];
              lastMsg.innerHTML = doubleCheck;
              await sleep(300);
              if (!isRunning) return;
              const tick = lastMsg.querySelector('.tick-icon');
              if (tick) tick.classList.add('check-read');
            }
            continue;
          }

          // Remove typing indicator before inserting real message
          const existingTyping = chatContainer.querySelector('.typing-indicator');
          if (existingTyping && action.type !== 'typing') {
            existingTyping.style.opacity = '0';
            existingTyping.style.transform = 'translateY(10px) scale(0.9)';
            existingTyping.style.transition = 'all 0.3s ease';
            await sleep(320);
            if (!isRunning) return;
            if (existingTyping.parentNode) existingTyping.remove();
          }

          if (action.type === 'typing' && existingTyping) continue;

          const gen = generators[action.type];
          if (!gen) continue;
          const htmlStr = gen(action.text);
          const template = document.createElement('template');
          template.innerHTML = htmlStr.trim();
          const newElement = template.content.firstChild;

          chatContainer.appendChild(newElement);
          void newElement.offsetWidth;
          newElement.classList.add('show');

          // Animate progress bar in bot_card
          if (action.type === 'bot_card') {
            setTimeout(() => {
              if (!isRunning) return;
              const bar = newElement.querySelector('#progress-bar');
              if (bar) bar.style.width = '45%';
            }, 500);
          }

          // Start waveform animation for audio message
          if (action.type === 'user_audio') {
            setTimeout(() => {
              if (!isRunning) return;
              newElement.querySelectorAll('.wv-bar').forEach(b => b.classList.add('wv-playing'));
            }, 300);
          }

          // Scroll-evict old messages (keep max 5 visible)
          const allMessages = chatContainer.querySelectorAll('.msg-bubble:not(.fade-up)');
          if (allMessages.length > 5) {
            const msgToRemove = allMessages[0];
            msgToRemove.classList.remove('show');
            msgToRemove.classList.add('fade-up');
            setTimeout(() => { if (isRunning && msgToRemove.parentNode) msgToRemove.remove(); }, 800);
          }
        }
      }
    }

    runAnimationSequence();

    return () => {
      isRunning = false;
      if (chatContainer) chatContainer.innerHTML = '';
    };
  }, []);

  return (
    <div className="w-full min-w-[320px] max-w-[420px] mx-auto z-10 relative pt-10 h-[460px] flex flex-col justify-start">
      {/* Contact Header con Avatar */}
      <div className="flex flex-col items-center gap-3 mb-6 relative z-20">
        <div className="relative">
          <img src="/landing/avatar-aorra.png" alt="Aorra Bot" className="w-24 h-24 rounded-full border-4 border-white shadow-xl object-cover bg-blue-50" />
          <div className="absolute bottom-1 right-1 w-5 h-5 bg-[#22c55e] rounded-full border-4 border-white"></div>
        </div>
        <div className="text-center">
          <h4 className="font-bold text-[#1e293b] text-lg m-0">Aorra AI</h4>
          <p className="text-sm text-[#22c55e] font-medium m-0">En línea</p>
        </div>
      </div>

      <div id="floating-chat" ref={chatContainerRef}></div>
    </div>
  );
};

export default FloatingChatAnimation;
